import { useSyncExternalStore } from "react";
import { safeLocalStorageGet, safeLocalStorageSet } from "../storage.js";
import { en } from "./en.js";
import { dictionaries, loadDictionary } from "./dictionaries.js";
import { LANGUAGE_STORAGE_KEY, initialLanguage } from "./dictionaryPreload.js";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES, resolveLanguage } from "./languages.js";

/** The set of translatable keys — the English dictionary is the source of truth. */
export type TranslationKey = keyof typeof en;

/**
 * UI language lives in a module-level store (not a React context) so any
 * component can read it via `useLanguage()` without the whole tree needing a
 * provider — which keeps the existing `<App />` test render working untouched
 * and defaults everything to English.
 *
 * A visit starts in the saved choice, else the browser's language, else English
 * — the rule index.html's inline script shares to start that language's
 * dictionary download early (dictionaryPreload.ts).
 */
function detectInitialLanguage(): string {
  const browser = typeof navigator !== "undefined" ? navigator.languages ?? [navigator.language] : [];
  return initialLanguage(safeLocalStorageGet(LANGUAGE_STORAGE_KEY), browser, SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE);
}

let currentLanguage = detectInitialLanguage();
let revision = 0;
const listeners = new Set<() => void>();

// Reflect the language on <html lang> for accessibility / correct hyphenation.
function syncDocumentLang(lang: string): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
  }
}
syncDocumentLang(currentLanguage);

export function getLanguage(): string {
  return currentLanguage;
}

export function setLanguage(raw: string): Promise<void> {
  const next = resolveLanguage(raw) ?? DEFAULT_LANGUAGE;
  if (next === currentLanguage && dictionaries[next]) return Promise.resolve();
  currentLanguage = next;
  safeLocalStorageSet(LANGUAGE_STORAGE_KEY, next);
  syncDocumentLang(next);
  revision += 1;
  listeners.forEach((listener) => listener());
  return loadDictionary(next).then(() => {
    if (currentLanguage !== next) return;
    revision += 1;
    listeners.forEach((listener) => listener());
  }).catch(() => {
    // Offline or a stale deployment chunk: English remains usable, and choosing
    // the language again retries the import instead of breaking the app.
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Reactive current language; components re-render when it changes. */
export function useLanguage(): string {
  useSyncExternalStore(subscribe, () => revision, () => revision);
  return currentLanguage;
}

// `{count|one:scene|few:сцены|many:сцен|other:сцены}` — a plural placeholder:
// the named numeric param picks a form by the language's CLDR plural category
// (Intl.PluralRules), falling back to `other`. English-style `{s}` suffixes
// cannot express Russian/Polish-style plurals, and read as a stray "s" in
// languages without plural inflection.
const PLURAL_PLACEHOLDER = /\{(\w+)((?:\|\w+:[^|}]*)+)\}/g;

const pluralRulesCache = new Map<string, Intl.PluralRules>();
function pluralCategory(lang: string, value: number): string {
  let rules = pluralRulesCache.get(lang);
  if (!rules) {
    try {
      rules = new Intl.PluralRules(lang);
    } catch {
      rules = new Intl.PluralRules("en");
    }
    pluralRulesCache.set(lang, rules);
  }
  return rules.select(value);
}

/**
 * Translate `key` for `lang`, falling back to English and then the raw key.
 * `params` fills `{name}` placeholders and `{name|one:…|other:…}` plural forms.
 * Pure — usable outside React too.
 */
export function translate(
  lang: string,
  key: TranslationKey,
  params?: Record<string, string | number>
): string {
  const dict = dictionaries[lang];
  const fromDict = dict?.[key];
  const template = fromDict ?? en[key] ?? key;
  if (!params) return template;
  // A template that fell back to English is pluralised by English rules.
  const pluralLang = fromDict !== undefined ? lang : "en";
  return template
    .replace(PLURAL_PLACEHOLDER, (match, name: string, forms: string) => {
      if (!(name in params)) return match;
      const byCategory = new Map(
        forms
          .slice(1)
          .split("|")
          .map((form) => {
            const colon = form.indexOf(":");
            return [form.slice(0, colon), form.slice(colon + 1)] as const;
          })
      );
      const category = pluralCategory(pluralLang, Number(params[name]));
      return byCategory.get(category) ?? byCategory.get("other") ?? match;
    })
    .replace(/\{(\w+)\}/g, (match, name: string) =>
      name in params ? String(params[name]) : match
    );
}

export type TranslateFn = (
  key: TranslationKey,
  params?: Record<string, string | number>
) => string;

/** Hook returning a `t()` bound to the current language. */
export function useT(): TranslateFn {
  const lang = useLanguage();
  return (key, params) => translate(lang, key, params);
}
